import {
  FilterKeys,
  VehicleYearFilterState,
  VehicleFilterPairValues,
  FilterActions,
  FilterAction,
  VehicleFilter,
} from "@modules/vehicle/types/vehicleFilter";
import Slider from "@components/form/Slider";
import FilterDisclosure from "@modules/vehicle/components/FilterDisclosure";
import { useEffect, useMemo, useState } from "react";
import Listbox from "@components/form/Listbox";
import { useTranslation } from "next-i18next";

export interface VehicleYearFilterProps {
  defaultMin: number;
  defaultMax: number;
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
}

const VehicleYearFilter = ({
  defaultMin,
  defaultMax,
  onUpdate,
  vehicleFilters,
}: VehicleYearFilterProps) => {
  const { t } = useTranslation(["common"]);

  const vehicleYearRange: VehicleFilterPairValues[] = useMemo(() => {
    const yearPairs: VehicleFilterPairValues[] = [];
    for (let i = defaultMin; i <= defaultMax; i += 1) {
      yearPairs.push({ id: i.toString(), value: i });
    }
    return yearPairs;
  }, [defaultMax, defaultMin]);

  const defaultMinPair = vehicleYearRange[0];
  const defaultMaxPair = vehicleYearRange[vehicleYearRange.length - 1];

  const yearFilter = vehicleFilters.find((f) => f.key === FilterKeys.Year);
  const yearFilterState = yearFilter
    ? (yearFilter.state as VehicleYearFilterState)
    : null;

  const userModified = yearFilter !== undefined;

  const [minVehicleYear, setMinVehicleYear] = useState(
    yearFilterState !== null ? yearFilterState.minYear : defaultMinPair
  );
  const [maxVehicleYear, setMaxVehicleYear] = useState(
    yearFilterState !== null ? yearFilterState.maxYear : defaultMaxPair
  );

  const updateSharedState = (
    minYearValue: VehicleFilterPairValues,
    maxYearValue: VehicleFilterPairValues
  ) => {
    const isDefault =
      minYearValue.value === defaultMinPair.value &&
      maxYearValue.value === defaultMaxPair.value;
    if (isDefault) {
      onUpdate({
        type: FilterActions.RemoveFilter,
        payload: FilterKeys.Year,
      });
    } else {
      onUpdate({
        type: FilterActions.AddYearFilter,
        payload: { minYear: minYearValue, maxYear: maxYearValue },
      });
    }
  };

  const findYearInRange = (
    value: number
  ): VehicleFilterPairValues | undefined => {
    return vehicleYearRange.find((x) => x.value === value);
  };

  useEffect(() => {
    if (yearFilterState !== null) {
      setMinVehicleYear(yearFilterState.minYear);
      setMaxVehicleYear(yearFilterState.maxYear);
    } else {
      setMinVehicleYear(defaultMinPair);
      setMaxVehicleYear(defaultMaxPair);
    }
  }, [defaultMinPair, defaultMaxPair, yearFilterState]);

  const onChangeMinYear = (changeValue: VehicleFilterPairValues) => {
    if (changeValue.value <= maxVehicleYear.value) {
      updateSharedState(changeValue, maxVehicleYear);
      setMinVehicleYear(changeValue);
      setMaxVehicleYear(maxVehicleYear);
    }
  };

  const onChangeMaxYear = (changeValue: VehicleFilterPairValues) => {
    if (changeValue.value >= minVehicleYear.value) {
      updateSharedState(minVehicleYear, changeValue);
      setMinVehicleYear(minVehicleYear);
      setMaxVehicleYear(changeValue);
    }
  };

  const onAfterSliderChange = (
    sliderChangeValue: number | readonly number[]
  ) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minYearValue = findYearInRange(sliderChangeValueArray[0]);
    const maxYearValue = findYearInRange(sliderChangeValueArray[1]);

    if (minYearValue && maxYearValue) {
      updateSharedState(minYearValue, maxYearValue);
    }
  };

  const onSliderChange = (sliderChangeValue: number | readonly number[]) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minYearValue = findYearInRange(sliderChangeValueArray[0]);
    const maxYearValue = findYearInRange(sliderChangeValueArray[1]);

    if (minYearValue !== undefined && maxYearValue !== undefined) {
      setMaxVehicleYear(maxYearValue);
      setMinVehicleYear(minYearValue);
    }
  };

  return (
    <FilterDisclosure active={userModified} headerText={t("common:year")}>
      <div>
        <div className="vehicle-year-filter">
          <div className="flex gap-4">
            <div
              className={`w-full ${
                minVehicleYear.value > defaultMinPair.value
                  ? "text-primary-bold"
                  : ""
              }`}
            >
              {/* label needs tracking-widest to match mock but is applying caption-1 */}
              <Listbox
                label={t("common:from")}
                key="min-year"
                items={vehicleYearRange}
                keyOption={(item) => item?.id}
                displayOption={(item) => item?.value || ""}
                displayButton={(item) => item?.value || ""}
                valueOption={(item) => item}
                selectedItem={minVehicleYear}
                onChange={onChangeMinYear}
              />
            </div>

            <div
              className={`w-full ${
                maxVehicleYear.value < defaultMaxPair.value
                  ? "text-primary-bold"
                  : ""
              }`}
            >
              <Listbox
                label={t("common:to")}
                items={vehicleYearRange}
                keyOption={(item) => item?.id}
                displayOption={(item) => item?.value || ""}
                displayButton={(item) => item?.value || ""}
                valueOption={(item) => item}
                selectedItem={maxVehicleYear}
                onChange={onChangeMaxYear}
              />
            </div>
          </div>

          <div className="pt-2">
            <Slider
              className="horizontal-slider"
              min={defaultMinPair.value}
              max={defaultMaxPair.value}
              value={[minVehicleYear.value, maxVehicleYear.value]}
              defaultValue={[defaultMinPair.value, defaultMaxPair.value]}
              onChange={onSliderChange}
              onAfterChange={onAfterSliderChange}
              ariaLabel="Vehicle Year Slider"
            />
          </div>
        </div>
        <div />
      </div>
    </FilterDisclosure>
  );
};
export default VehicleYearFilter;
